import { ReactNode } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Grid, Typography, List, ListItem, Box, Theme } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { useFormikContext } from 'formik'
import {
  SemiConfigFormValues,
  useProductFromQueryParam,
  getInitialValuesBasedOnProduct,
} from '../ProductsConfiguration'
import useSearch from '../useSearch'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import HoverToolTip from '@src/components/HoverToolTip'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingTop: theme.spacing(8),
  },
  instructions: {
    padding: theme.spacing(6, 0),
    '& h6': {
      fontWeight: 700,
    },
  },
  checkBoxes: {
    display: 'flex',
  },
  titleWithInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  infoList: {
    paddingLeft: theme.spacing(5),
  },
  criteriaListItem: {
    fontWeight: 400,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    '&.MuiListItem-root': {
      listStyle: 'disc',
      display: 'list-item',
    },
  },
  popperClass: {
    maxWidth: 'none',
  },
  tooltipClass: {
    maxWidth: 'none',
    width: 'auto',
    padding: theme.spacing(4),
  },
}))

const GRnaCategory = () => {
  const classes = useStyles()
  const product = useProductFromQueryParam()
  const { searchQuery } = useSearch()
  const formik = useFormikContext<SemiConfigFormValues>()
  const initialValues = getInitialValuesBasedOnProduct(product)
  const categories: { name: string; label: ReactNode }[] = [
    {
      name: 'Recommended',
      label: (
        <FormattedMessage
          id="SEMI_CONF_TOP_CHOICE_crispgrna"
          defaultMessage="Top Choice"
        />
      ),
    },
    {
      name: 'positiveControl',
      label: (
        <FormattedMessage
          id="SEMI_CONF_POSITIVE_CONTROL_crispgrna"
          defaultMessage="Positive Control"
        />
      ),
    },
    {
      name: 'negativeControl',
      label: (
        <FormattedMessage
          id="SEMI_CONF_NEGATIVE_CONTROL_crispgrna"
          defaultMessage="Negative Control"
        />
      ),
    },
  ]

  const onCategoryChange: React.ChangeEventHandler<HTMLInputElement> = async (
    e
  ) => {
    let updatedFacet: string[] = []
    if (e.target.checked) {
      updatedFacet = [
        ...formik.values.searchData.facet,
        e.target.value === 'Recommended'
          ? `recommendedStatus:${e.target.value}`
          : `${e.target.value}:${e.target.value}`,
      ]
    } else {
      updatedFacet = formik.values.searchData.facet.filter(
        (facet) =>
          facet !== `${e.target.value}:${e.target.value}` &&
          facet !== `recommendedStatus:${e.target.value}`
      )
    }
    formik.setFieldValue('searchData.facet', updatedFacet, false)
    formik.setFieldValue('selectedClones', [], false)
    formik.setFieldValue('specifications', initialValues.specifications, false)
    //trigger search
    await searchQuery({ facet: updatedFacet })
  }
  const isChecked: (e: string) => boolean = (name) => {
    const facet = formik.values.searchData.facet
    return (
      facet.includes(`${name}:${name}`) ||
      facet.includes(`recommendedStatus:${name}`)
    )
  }

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12} container>
        <Box className={classes.titleWithInfo}>
          <Typography variant="h3">
            <FormattedMessage
              id="CONF_CATEGORY_crisprgrna"
              defaultMessage="gRNA Category"
            />
          </Typography>
          <HoverToolTip
            fontSize="19.25"
            popperClass={classes.popperClass}
            tooltipClass={classes.tooltipClass}
            toolTipText={
              <Box>
                <Typography variant="body2">
                  <FormattedMessage
                    id="SEMICONF_HELP_ICON_TEXT_CATEGORY_crisprgrna"
                    defaultMessage="Predesigned gRNAs are recommended for KO applications based on the following design criteria:"
                  />
                </Typography>
                <List className={classes.infoList}>
                  <ListItem className={classes.criteriaListItem} disableGutters>
                    <FormattedMessage
                      id="SEMICONF_HELP_ICON_TEXT_CATEGORY_POINT1_crisprgrna"
                      defaultMessage="All targets must have a minimum of 1bp mismatch to the rest of the genome"
                    />
                  </ListItem>
                  <ListItem className={classes.criteriaListItem} disableGutters>
                    <FormattedMessage
                      id="SEMICONF_HELP_ICON_TEXT_CATEGORY_POINT2_CRISPRGRNA_UPDATED"
                      defaultMessage="All targets must be within the first 2/3 of the coding region, and exclude the first 90bp 3’ of ATG start codon"
                    />
                  </ListItem>
                  <ListItem className={classes.criteriaListItem} disableGutters>
                    <FormattedMessage
                      id="SEMICONF_HELP_ICON_TEXT_CATEGORY_POINT3_crisprgrna"
                      defaultMessage="gRNAs with 5 or more consecutive T bases are eliminated"
                    />
                  </ListItem>
                  <ListItem className={classes.criteriaListItem} disableGutters>
                    <FormattedMessage
                      id="SEMICONF_HELP_ICON_TEXT_CATEGORY_POINT4_crisprgrna"
                      defaultMessage="No overlapping gRNAs"
                    />
                  </ListItem>
                  <ListItem className={classes.criteriaListItem} disableGutters>
                    <FormattedMessage
                      id="SEMICONF_HELP_ICON_TEXT_CATEGORY_POINT5_crisprgrna"
                      defaultMessage="Where possible all targets must be in exons conserved across all NM and XM transcripts"
                    />
                  </ListItem>
                  <ListItem className={classes.criteriaListItem} disableGutters>
                    <FormattedMessage
                      id="SEMICONF_HELP_ICON_TEXT_CATEGORY_POINT6_crisprgrna"
                      defaultMessage='"Top Choice" guides target the maximum number of transcripts and have the highest efficiency and specificity scores'
                    />
                  </ListItem>
                </List>
              </Box>
            }
          />
        </Box>
      </Grid>
      <Grid item xs={12} className={classes.instructions}>
        <Typography variant="subtitle1">
          <FormattedMessage
            id="CONF_CATEGORY_CRISPRGRNA_FILTER_SUB_TITLE"
            defaultMessage="Select a Category to Filter Results"
          />
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.checkBoxes}>
        {categories.map((category) => (
          <LiquidCheckbox
            key={category.name}
            aria-label={category.name}
            value={category.name}
            checked={isChecked(category.name)}
            onChange={onCategoryChange}
            label={category.label}
          />
        ))}
      </Grid>
    </Grid>
  )
}

export default GRnaCategory
